import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { 
  Download, 
  ArrowLeft,
  Play,
  BookOpen,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "How To Mod Stellar Blade (PC Version) - Installation Guide" },
    { 
      name: "description", 
      content: "Complete step-by-step video guide on how to mod Stellar Blade PC version. Learn modding techniques with detailed tutorial and instructions." 
    },
    { name: "keywords", content: "stellar blade,mod installation,pc modding,tutorial,video guide,step by step" },
    { name: "robots", content: "index,follow" },
    { name: "language", content: "en" },
    { rel: "canonical", href: "https://stellarblademod.com/wiki/knowledge-base/installation" },
    
    // Language alternates
    { tagName: "link", rel: "alternate", hrefLang: "en", href: "https://stellarblademod.com/wiki/knowledge-base/installation" },
    { tagName: "link", rel: "alternate", hrefLang: "ja", href: "https://stellarblademod.com/ja/wiki/knowledge-base/installation" },
    { tagName: "link", rel: "alternate", hrefLang: "ko", href: "https://stellarblademod.com/ko/wiki/knowledge-base/installation" },
    { tagName: "link", rel: "alternate", hrefLang: "x-default", href: "https://stellarblademod.com/wiki/knowledge-base/installation" },
    
    // Open Graph
    { property: "og:title", content: "How To Mod Stellar Blade (PC Version) - Installation Guide" },
    { property: "og:description", content: "Complete step-by-step video guide on how to mod Stellar Blade PC version. Learn modding techniques with detailed tutorial and instructions." },
    { property: "og:url", content: "https://stellarblademod.com/wiki/knowledge-base/installation" },
    { property: "og:type", content: "article" },
    { property: "og:locale", content: "en_US" },
    { property: "og:image", content: "https://img.youtube.com/vi/w8UAWqIitQQ/maxresdefault.jpg" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "How To Mod Stellar Blade (PC Version) - Installation Guide" },
    { name: "twitter:description", content: "Complete step-by-step video guide on how to mod Stellar Blade PC version. Learn modding techniques with detailed tutorial and instructions." },
    { name: "twitter:image", content: "https://img.youtube.com/vi/w8UAWqIitQQ/maxresdefault.jpg" },
    
    { httpEquiv: "content-language", content: "en" },
  ];
};

export default function InstallationGuide() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <div className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-8">
            <Link to="/wiki" className="text-cyan-400 hover:text-cyan-300">
              Wiki
            </Link>
            <span className="text-gray-500">/</span>
            <Link to="/wiki/knowledge-base" className="text-cyan-400 hover:text-cyan-300">
              Knowledge Base
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-gray-300">Installation</span>
          </nav>

          {/* Back Button */}
          <Link 
            to="/wiki/knowledge-base"
            className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Knowledge Base
          </Link>

          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <Download className="w-12 h-12 text-green-400 mr-4" />
              <div>
                                 <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                   How To Mod Stellar Blade
                 </h1>
                <p className="text-xl text-gray-400">(PC Version)</p>
              </div>
            </div>
            
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              Video guide step-by-step tutorial on How To Mod Stellar Blade
            </p>

            {/* Article Meta */}
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-400 mb-8">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                <span>5 min read</span>
              </div>
              <div className="flex items-center">
                <Eye className="w-4 h-4 mr-1" />
                <span>25.3k views</span>
              </div>
              <div className="flex items-center">
                <BookOpen className="w-4 h-4 mr-1" />
                <span>Installation Guide</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Video Section */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Play className="w-6 h-6 mr-2 text-green-400" />
            Video Tutorial
          </h2>
          
          {/* YouTube Video Embed */}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/w8UAWqIitQQ"
              title="How To Mod Stellar Blade (PC Version)"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>
          
          <p className="text-gray-300 mt-4 text-center">
            Watch the complete video tutorial: <a href="https://www.youtube.com/watch?v=w8UAWqIitQQ" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">How To Mod Stellar Blade (PC Version)</a>
          </p>
        </div>

        {/* Prerequisites Section */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <CheckCircle className="w-6 h-6 mr-2 text-green-400" />
            Prerequisites
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold">Stellar Blade PC Version</h3>
                <p className="text-gray-300">Make sure you have the official PC version of Stellar Blade installed</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold">Backup Your Save Files</h3>
                <p className="text-gray-300">Always create backups of your save files before installing any mods</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold">Administrator Access</h3>
                <p className="text-gray-300">Some mod installations may require administrator privileges</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold">Basic Computer Knowledge</h3>
                <p className="text-gray-300">Familiarity with file management and folder navigation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Installation Steps */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Download className="w-6 h-6 mr-2 text-green-400" />
            Installation Steps
          </h2>
          
          <div className="space-y-6">
            <div className="border-l-4 border-green-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 1: Download and Verify Files</h3>
              <p className="text-gray-300 mb-3">
                Follow the video tutorial to download the necessary modding tools and mod files. 
                Before installation, upload downloaded files to <a href="https://www.virustotal.com/gui/home/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">VirusTotal</a> for security scanning.
                The video will show you exactly which tools you need and where to download them safely.
              </p>
            </div>
            
            <div className="border-l-4 border-cyan-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 2: Prepare Game Directory</h3>
              <p className="text-gray-300 mb-3">
                The tutorial demonstrates how to locate your Stellar Blade installation directory 
                and prepare it for mod installation.
              </p>
            </div>
            
            <div className="border-l-4 border-purple-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 3: Install Mods</h3>
              <p className="text-gray-300 mb-3">
                Follow along with the video as it shows the complete mod installation process, 
                including file placement and configuration.
              </p>
            </div>
            
            <div className="border-l-4 border-yellow-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 4: Test Installation</h3>
              <p className="text-gray-300 mb-3">
                Learn how to verify that your mods are working correctly and troubleshoot 
                any potential issues that may arise.
              </p>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <AlertTriangle className="w-6 h-6 mr-2 text-yellow-400" />
            Important Notes
          </h2>
          
          <div className="space-y-4 text-gray-300">
                         <p>
               <strong className="text-yellow-400">Always backup your files:</strong> Before installing any mods, 
               create backups of your save files and important game files.
             </p>
             <p>
               <strong className="text-yellow-400">Scan for security:</strong> Use <a href="https://www.virustotal.com/gui/home/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">VirusTotal</a> to scan mod files before installation. This free service uses 70+ antivirus engines to detect potential threats.
             </p>
            <p>
              <strong className="text-yellow-400">Use at your own risk:</strong> Modding can potentially 
              cause game instability or save file corruption. Proceed with caution.
            </p>
            <p>
              <strong className="text-yellow-400">Check compatibility:</strong> Ensure that the mods you're 
              installing are compatible with your version of Stellar Blade.
            </p>
            <p>
              <strong className="text-yellow-400">One mod at a time:</strong> Install mods one by one to 
              easily identify any conflicts or issues.
            </p>
          </div>
        </div>

        {/* Related Articles */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Related Articles</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/wiki/knowledge-base/troubleshooting" className="group">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-yellow-400/50">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-yellow-300">
                  Troubleshooting Common Issues
                </h3>
                <p className="text-gray-400 text-sm">
                  Fix installation problems and mod conflicts
                </p>
              </div>
            </Link>
            
            <Link to="/wiki/knowledge-base/tools" className="group">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-indigo-400/50">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-indigo-300">
                  Essential Modding Tools
                </h3>
                <p className="text-gray-400 text-sm">
                  Must-have tools for Stellar Blade modding
                </p>
              </div>
            </Link>
            
            <Link to="/wiki/faq" className="group">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-purple-400/50">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-300">
                  Frequently Asked Questions
                </h3>
                <p className="text-gray-400 text-sm">
                  Quick answers to common modding questions
                </p>
              </div>
            </Link>
            
            <Link to="/wiki/knowledge-base/advanced" className="group">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:border-cyan-400/50">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-300">
                  Advanced Techniques
                </h3>
                <p className="text-gray-400 text-sm">
                  Expert-level modding and customization
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 